export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <a
          href="/"
          className="mb-8 inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300"
        >
          &larr; Back to OpenQuote Pro
        </a>

        <h1 className="mb-2 text-3xl font-bold text-white">Privacy Policy</h1>
        <p className="mb-10 text-sm text-gray-400">Last updated: March 1, 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed text-gray-300">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">1. Who We Are</h2>
            <p>
              OpenQuote Pro is a product of OpenWare (&ldquo;we,&rdquo; &ldquo;us,&rdquo;
              &ldquo;our&rdquo;). We provide a multi-tenant quoting platform that enables
              manufacturers and resellers to create, manage, and share product quotes. Our website
              is located at{' '}
              <a href="https://openquote.pro" className="text-brand-400 hover:underline">
                openquote.pro
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">2. Information We Collect</h2>
            <p className="mb-3">We collect the following types of information:</p>
            <p className="mb-2">
              <strong className="text-gray-100">Account Information:</strong> When you create an
              account, we collect your name, email address, phone number (optional), organization
              name, and password (stored as a cryptographic hash, never in plain text).
            </p>
            <p className="mb-2">
              <strong className="text-gray-100">Business Data:</strong> Product catalogs, pricing
              information, reseller records, contacts, and quotes you create within the platform.
              This data belongs to your organization and is isolated within your tenant.
            </p>
            <p className="mb-2">
              <strong className="text-gray-100">Usage Data:</strong> We collect analytics on shared
              quote views and downloads, including IP addresses, user agent strings, and
              timestamps, to provide you with engagement insights on your shared content.
            </p>
            <p>
              <strong className="text-gray-100">Communications Data:</strong> If you provide a
              phone number, we may send transactional SMS messages including verification codes,
              quote delivery notifications, and account security alerts.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">
              3. How We Use Your Information
            </h2>
            <p className="mb-3">We use collected information to:</p>
            <p className="mb-1">
              &bull; Provide, maintain, and improve the OpenQuote Pro platform
            </p>
            <p className="mb-1">
              &bull; Authenticate your identity and secure your account
            </p>
            <p className="mb-1">
              &bull; Send transactional emails and SMS notifications you have opted into
            </p>
            <p className="mb-1">
              &bull; Provide quote sharing analytics (view and download counts)
            </p>
            <p>&bull; Respond to your support requests</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">4. SMS Messaging</h2>
            <p className="mb-3">
              By providing your phone number during account registration or in your profile
              settings, you consent to receive transactional SMS messages from OpenQuote Pro.
              These messages include:
            </p>
            <p className="mb-1">&bull; Account verification and password reset codes</p>
            <p className="mb-1">&bull; Quote delivery and share link notifications</p>
            <p className="mb-3">&bull; Account security alerts</p>
            <p className="mb-3">
              We do not send marketing or promotional SMS messages. All SMS messages are triggered
              by actions you or other users take within the platform.
            </p>
            <p>
              <strong className="text-gray-100">Opt-out:</strong> You can stop receiving SMS
              messages at any time by replying STOP to any message. You can also remove your phone
              number from your account profile. Standard message and data rates may apply.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">5. Data Sharing</h2>
            <p className="mb-3">
              We do not sell, rent, or trade your personal information to third parties. We share
              data only in the following circumstances:
            </p>
            <p className="mb-2">
              <strong className="text-gray-100">Service Providers:</strong> We use third-party
              services to operate the platform, including email delivery (Resend/Amazon SES) and
              SMS messaging (Twilio). These providers process data only as necessary to deliver
              their services and are bound by their own privacy policies.
            </p>
            <p className="mb-2">
              <strong className="text-gray-100">Cross-Tenant Sharing:</strong> When you send a
              quote to a reseller or create a public share link, the quote data you choose to share
              becomes visible to the intended recipients. You control what is shared and with whom.
            </p>
            <p>
              <strong className="text-gray-100">Legal Requirements:</strong> We may disclose
              information if required by law, regulation, or legal process.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">6. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your data, including
              encrypted connections (HTTPS/TLS), hashed passwords, JWT-based authentication, and
              tenant-level data isolation. All business data is scoped to your organization and
              cannot be accessed by other tenants.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">7. Data Retention</h2>
            <p>
              We retain your account and business data for as long as your account is active. If
              you delete your account, we will remove your personal information and organization
              data within 30 days. Anonymized usage analytics may be retained indefinitely.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">8. Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <p className="mb-1">
              &bull; Access the personal information we hold about you
            </p>
            <p className="mb-1">
              &bull; Correct inaccurate information in your account profile
            </p>
            <p className="mb-1">
              &bull; Export your business data (quote JSON export)
            </p>
            <p className="mb-1">
              &bull; Request deletion of your account and associated data
            </p>
            <p>&bull; Opt out of SMS notifications at any time</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify registered users
              of significant changes via email or in-app notification. Continued use of the
              platform after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">10. Contact</h2>
            <p>
              If you have questions about this Privacy Policy or your data, contact us at{' '}
              <a
                href="mailto:privacy@openquote.pro"
                className="text-brand-400 hover:underline"
              >
                privacy@openquote.pro
              </a>
              .
            </p>
          </section>
        </div>

        <div className="mt-12 border-t border-gray-800 pt-6 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} OpenWare. All rights reserved.
        </div>
      </div>
    </div>
  );
}
